import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(...registerables, ChartDataLabels);

const IndividualTLFootfallChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Default data if none provided
    const defaultData = [
        { label: 'Mon', footfall: 50 },
        { label: 'Tue', footfall: 40 },
        { label: 'Wed', footfall: 60 },
        { label: 'Thu', footfall: 50 },
        { label: 'Fri', footfall: 70 },
        { label: 'Sat', footfall: 80 },
        { label: 'Sun', footfall: 80 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.label);
    const values = chartData.map(item => item.footfall || 0);
    const maxVal = Math.max(...values, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const barThickness = isMobile ? 18 : isTablet ? 20 : 24;
            const fontSize = isMobile ? 10 : isTablet ? 11 : 12;
            const labelFontSize = isMobile ? 11 : isTablet ? 12 : 13;


            const ctx = chartRef.current.getContext('2d');
            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Footfall',
                            data: values,
                            backgroundColor: 'rgba(255, 179, 207, 0.4)',
                            borderColor: '#ff448c',
                            borderWidth: 1,
                            barThickness: barThickness,
                            borderRadius: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: {
                            display: (context) => context.active,
                            align: 'top',
                            anchor: 'end',
                            offset: isMobile ? 2 : 4,
                            color: '#ff448c',
                            font: {
                                family: '"Inter", sans-serif',
                                size: labelFontSize,
                                weight: '700',
                            },
                            formatter: (value) => value.toLocaleString(),
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                color: '#333333',
                                font: { size: fontSize },
                                maxRotation: isMobile ? 45 : 0,
                                minRotation: isMobile ? 45 : 0,
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                precision: 0,
                                color: '#4d4d4d',
                                font: { size: fontSize },
                                maxTicksLimit: isMobile ? 5 : 10,
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, data, labels, values, suggestedMax]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default IndividualTLFootfallChart;
